import { useEffect, useState } from "react";
import api, { formatIDR, formatDate } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Trash2, X, Printer, Link2, Wallet, Copy, Check } from "lucide-react";
import { toast } from "sonner";

export function InvoicePage({ kind, title }) {
  const [items, setItems] = useState([]);
  const [parties, setParties] = useState([]);
  const [products, setProducts] = useState([]);
  const [open, setOpen] = useState(false);
  const [view, setView] = useState(null);
  const [payments, setPayments] = useState([]);
  const [copied, setCopied] = useState(false);
  const [shareUrl, setShareUrl] = useState("");
  const empty = { kind, party_id: "", date: new Date().toISOString().slice(0,10), due_date: "", tax_rate: 11, notes: "", status: "unpaid", items: [] };
  const [form, setForm] = useState(empty);
  const emptyPay = { amount: "", date: new Date().toISOString().slice(0,10), method: "transfer", reference: "", notes: "" };
  const [pay, setPay] = useState(emptyPay);
  const [showPay, setShowPay] = useState(false);

  const load = () => api.get(`/invoices?kind=${kind}`).then(r=>setItems(r.data));
  useEffect(()=>{
    load();
    api.get(`/parties?kind=${kind==="sales"?"client":"supplier"}`).then(r=>setParties(r.data));
    api.get("/products").then(r=>setProducts(r.data));
  },[kind]);

  const loadPayments = (id) => api.get(`/invoices/${id}/payments`).then(r=>setPayments(r.data));
  const openView = async (inv) => {
    setView(inv); setShareUrl(""); await loadPayments(inv.id);
    if (inv.share_token) setShareUrl(`${window.location.origin}/public/invoice/${inv.share_token}`);
  };
  const refresh = async () => {
    const { data } = await api.get(`/invoices/${view.id}`);
    setView(data); await loadPayments(view.id); await load();
  };

  const addItem = () => setForm(f=>({...f, items:[...f.items, {product_id:"", name:"", qty:1, unit_price:0, discount:0}]}));
  const setItem = (i, field, val) => setForm(f=>{const items=[...f.items]; items[i]={...items[i], [field]:val}; return {...f, items};});
  const pickProduct = (i, pid) => {
    const p = products.find(x=>x.id===pid);
    setForm(f=>{const items=[...f.items]; items[i]={...items[i], product_id:pid, name:p?.name||"", unit_price:kind==="sales"?p?.price||0:p?.cost||0}; return {...f, items};});
  };
  const rmItem = (i) => setForm(f=>({...f, items:f.items.filter((_,x)=>x!==i)}));

  const subtotal = form.items.reduce((s,it)=>s + (it.qty||0)*(it.unit_price||0)*(1-(it.discount||0)/100), 0);
  const tax = subtotal * (form.tax_rate/100);
  const total = subtotal + tax;

  const save = async () => {
    if (!form.party_id) return toast.error("Pilih klien/supplier");
    if (form.items.length===0) return toast.error("Tambahkan minimal 1 item");
    try {
      const body = {...form, items: form.items.map(it=>({...it, qty:+it.qty, unit_price:+it.unit_price, discount:+(it.discount||0)}))};
      await api.post("/invoices", body); toast.success("Invoice dibuat"); setOpen(false); setForm(empty); load();
    } catch { toast.error("Gagal menyimpan"); }
  };
  const updateStatus = async (id, status) => { await api.put(`/invoices/${id}?status=${status}`); load(); };
  const remove = async (id) => { if(!confirm("Hapus?")) return; await api.delete(`/invoices/${id}`); load(); };

  const savePayment = async () => {
    if (!pay.amount || +pay.amount<=0) return toast.error("Isi jumlah");
    try {
      await api.post(`/invoices/${view.id}/payments`, {...pay, amount:+pay.amount});
      toast.success("Pembayaran tercatat"); setShowPay(false); setPay(emptyPay); refresh();
    } catch { toast.error("Gagal"); }
  };
  const delPayment = async (pid) => { await api.delete(`/payments/${pid}`); refresh(); };

  const share = async () => {
    const { data } = await api.post(`/invoices/${view.id}/share`);
    const url = `${window.location.origin}/public/invoice/${data.token}`;
    setShareUrl(url); toast.success("Tautan siap dibagikan");
  };
  const copy = () => { navigator.clipboard.writeText(shareUrl); setCopied(true); setTimeout(()=>setCopied(false), 1500); };
  const printDoc = () => window.open(`/print/invoice/${view.id}`, "_blank");

  const partyName = (id) => parties.find(p=>p.id===id)?.name || "-";

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between">
        <div>
          <div className="label-tag">Akuntansi</div>
          <h1 className="font-display text-4xl tracking-tight mt-1">{title}</h1>
          <p className="text-sm text-forest-400 mt-2">Kelola {kind==="sales"?"faktur penjualan":"purchase order / faktur pembelian"} dengan PPN 11%.</p>
        </div>
        <Dialog open={open} onOpenChange={(v)=>{setOpen(v); if(!v)setForm(empty);}}>
          <DialogTrigger asChild>
            <Button data-testid={`btn-add-${kind}-invoice`} className="rounded-sm bg-[#2D4739] hover:bg-[#22362B] gap-2"><Plus size={16}/> Buat {kind==="sales"?"Invoice":"PO"}</Button>
          </DialogTrigger>
          <DialogContent className="bg-[#FDFDFB] rounded-sm max-w-4xl max-h-[90vh] overflow-y-auto">
            <DialogHeader><DialogTitle className="font-display">Buat {kind==="sales"?"Invoice Penjualan":"Purchase Order"}</DialogTitle></DialogHeader>
            <div className="grid grid-cols-3 gap-3">
              <div><Label>{kind==="sales"?"Klien":"Supplier"}</Label>
                <Select value={form.party_id} onValueChange={(v)=>setForm({...form, party_id:v})}>
                  <SelectTrigger data-testid="sel-party"><SelectValue placeholder="Pilih..." /></SelectTrigger>
                  <SelectContent>{parties.map(p=><SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Tanggal</Label><Input type="date" value={form.date} onChange={e=>setForm({...form, date:e.target.value})}/></div>
              <div><Label>Jatuh Tempo</Label><Input type="date" value={form.due_date||""} onChange={e=>setForm({...form, due_date:e.target.value})}/></div>
            </div>
            <div className="mt-4">
              <div className="flex items-center justify-between mb-2">
                <div className="label-tag">Item</div>
                <Button size="sm" onClick={addItem} className="rounded-sm bg-[#151A18] hover:bg-[#22362B] gap-1 h-8"><Plus size={12}/> Tambah</Button>
              </div>
              <div className="space-y-2">
                {form.items.map((it, i) => (
                  <div key={i} className="grid grid-cols-12 gap-2 items-end border border-forest-100 p-2 rounded-sm">
                    <div className="col-span-4"><Label className="text-[10px]">Produk</Label>
                      <Select value={it.product_id||""} onValueChange={(v)=>pickProduct(i, v)}>
                        <SelectTrigger><SelectValue placeholder="Pilih atau ketik" /></SelectTrigger>
                        <SelectContent>{products.map(p=><SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                    <div className="col-span-3"><Label className="text-[10px]">Nama</Label><Input value={it.name} onChange={e=>setItem(i,"name",e.target.value)}/></div>
                    <div className="col-span-1"><Label className="text-[10px]">Qty</Label><Input type="number" value={it.qty} onChange={e=>setItem(i,"qty",e.target.value)}/></div>
                    <div className="col-span-2"><Label className="text-[10px]">Harga</Label><Input type="number" value={it.unit_price} onChange={e=>setItem(i,"unit_price",e.target.value)}/></div>
                    <div className="col-span-1"><Label className="text-[10px]">Disc%</Label><Input type="number" value={it.discount||0} onChange={e=>setItem(i,"discount",e.target.value)}/></div>
                    <div className="col-span-1"><button onClick={()=>rmItem(i)} className="p-1.5 text-[#C45A3D]"><X size={16}/></button></div>
                  </div>
                ))}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3 mt-4">
              <div><Label>Catatan</Label><Textarea value={form.notes||""} onChange={e=>setForm({...form, notes:e.target.value})}/></div>
              <div className="border border-forest-100 p-4 rounded-sm text-sm space-y-1">
                <div className="flex justify-between"><span className="text-forest-400">Subtotal</span><span className="num">{formatIDR(subtotal)}</span></div>
                <div className="flex justify-between"><span className="text-forest-400">PPN {form.tax_rate}%</span><span className="num">{formatIDR(tax)}</span></div>
                <div className="flex justify-between font-display text-lg pt-2 border-t border-forest-100"><span>Total</span><span className="num">{formatIDR(total)}</span></div>
              </div>
            </div>
            <DialogFooter><Button data-testid="btn-save-invoice" onClick={save} className="rounded-sm bg-[#2D4739] hover:bg-[#22362B]">Simpan</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="card-grid overflow-x-auto">
        <table className="w-full text-sm">
          <thead><tr className="border-b border-forest-100">
            <th className="text-left px-4 py-3 label-tag">No.</th>
            <th className="text-left px-4 py-3 label-tag">Tanggal</th>
            <th className="text-left px-4 py-3 label-tag">{kind==="sales"?"Klien":"Supplier"}</th>
            <th className="text-right px-4 py-3 label-tag">Total</th>
            <th className="text-right px-4 py-3 label-tag">Sisa</th>
            <th className="text-left px-4 py-3 label-tag">Status</th>
            <th className="text-right px-4 py-3 label-tag">Aksi</th>
          </tr></thead>
          <tbody>
            {items.length===0 && <tr><td colSpan={7} className="text-center py-12 text-forest-400">Belum ada dokumen</td></tr>}
            {items.map(inv => (
              <tr key={inv.id} className="data-row border-b border-forest-100 cursor-pointer" onClick={()=>openView(inv)}>
                <td className="px-4 py-3 font-mono text-xs">{inv.number}</td>
                <td className="px-4 py-3 text-forest-400">{formatDate(inv.date)}</td>
                <td className="px-4 py-3 font-medium">{partyName(inv.party_id)}</td>
                <td className="px-4 py-3 text-right num">{formatIDR(inv.total)}</td>
                <td className="px-4 py-3 text-right num text-[#C45A3D]">{formatIDR(inv.outstanding || 0)}</td>
                <td className="px-4 py-3">
                  {inv.status==="paid" ? <Badge className="bg-[#4A6B53] text-white rounded-sm">Lunas</Badge> :
                   inv.status==="cancelled" ? <Badge className="bg-forest-400 text-white rounded-sm">Batal</Badge> :
                   (inv.paid_amount||0) > 0 ? <Badge className="bg-[#D99C3B] text-white rounded-sm">Cicilan</Badge> :
                   <Badge className="bg-[#D99C3B] text-white rounded-sm">Belum</Badge>}
                </td>
                <td className="px-4 py-3 text-right" onClick={e=>e.stopPropagation()}>
                  <button onClick={()=>window.open(`/print/invoice/${inv.id}`,"_blank")} data-testid={`btn-print-${inv.id}`} className="p-1.5 hover:bg-forest-50" title="Cetak"><Printer size={14}/></button>
                  {inv.status!=="paid" && inv.status!=="cancelled" && <button onClick={()=>updateStatus(inv.id,"cancelled")} className="p-1.5 hover:bg-forest-50 text-xs">Batal</button>}
                  <button onClick={()=>remove(inv.id)} className="p-1.5 hover:bg-forest-50 text-[#C45A3D]"><Trash2 size={14}/></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Detail dialog */}
      <Dialog open={!!view} onOpenChange={(v)=>!v && setView(null)}>
        <DialogContent className="bg-[#FDFDFB] rounded-sm max-w-3xl max-h-[90vh] overflow-y-auto">
          {view && <>
            <DialogHeader><DialogTitle className="font-display flex items-center gap-3">{view.number}
              {view.status==="paid" && <Badge className="bg-[#4A6B53] text-white rounded-sm">Lunas</Badge>}
              {view.status==="cancelled" && <Badge className="bg-forest-400 text-white rounded-sm">Batal</Badge>}
            </DialogTitle></DialogHeader>

            <div className="flex flex-wrap gap-2 border-y border-forest-100 py-3">
              <Button size="sm" onClick={printDoc} data-testid="btn-detail-print" className="rounded-sm bg-[#151A18] hover:bg-[#22362B] gap-1"><Printer size={14}/> Cetak / PDF</Button>
              {kind==="sales" && <Button size="sm" onClick={share} data-testid="btn-detail-share" className="rounded-sm bg-[#2D4739] hover:bg-[#22362B] gap-1"><Link2 size={14}/> Bagikan Link</Button>}
              {view.status!=="paid" && view.status!=="cancelled" && <Button size="sm" onClick={()=>setShowPay(!showPay)} data-testid="btn-detail-add-payment" className="rounded-sm bg-[#D99C3B] hover:bg-[#c48a2f] gap-1 text-white"><Wallet size={14}/> Catat Pembayaran</Button>}
            </div>

            {shareUrl && (
              <div className="text-sm bg-[#F2F4F2] border border-forest-100 p-3 rounded-sm flex items-center gap-2">
                <input readOnly value={shareUrl} data-testid="share-url" className="flex-1 bg-transparent outline-none text-xs font-mono"/>
                <button onClick={copy} className="p-1.5 hover:bg-white rounded-sm">{copied ? <Check size={14} className="text-[#4A6B53]"/> : <Copy size={14}/>}</button>
              </div>
            )}

            {showPay && (
              <div className="card-grid p-4 space-y-3">
                <div className="label-tag">Catat Pembayaran / DP</div>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div><Label>Jumlah</Label><Input data-testid="inp-amount" type="number" value={pay.amount} onChange={e=>setPay({...pay, amount:e.target.value})}/></div>
                  <div><Label>Tanggal</Label><Input type="date" value={pay.date} onChange={e=>setPay({...pay, date:e.target.value})}/></div>
                  <div><Label>Metode</Label>
                    <Select value={pay.method} onValueChange={(v)=>setPay({...pay, method:v})}>
                      <SelectTrigger><SelectValue/></SelectTrigger>
                      <SelectContent><SelectItem value="transfer">Transfer</SelectItem><SelectItem value="cash">Tunai</SelectItem><SelectItem value="ewallet">E-Wallet</SelectItem><SelectItem value="other">Lainnya</SelectItem></SelectContent>
                    </Select>
                  </div>
                  <div><Label>Referensi</Label><Input value={pay.reference} onChange={e=>setPay({...pay, reference:e.target.value})}/></div>
                  <div className="col-span-2"><Button data-testid="btn-save-payment" onClick={savePayment} className="rounded-sm bg-[#2D4739] hover:bg-[#22362B] w-full">Simpan Pembayaran</Button></div>
                </div>
              </div>
            )}

            <div className="text-sm space-y-2">
              <div className="flex justify-between text-forest-400"><span>Tanggal</span><span className="text-[#151A18]">{formatDate(view.date)}</span></div>
              <div className="flex justify-between text-forest-400"><span>{kind==="sales"?"Klien":"Supplier"}</span><span className="text-[#151A18] font-medium">{partyName(view.party_id)}</span></div>
              <table className="w-full mt-3 border-t border-forest-100">
                <thead><tr className="border-b border-forest-100"><th className="text-left py-2 label-tag">Item</th><th className="text-right label-tag">Qty</th><th className="text-right label-tag">Harga</th><th className="text-right label-tag">Total</th></tr></thead>
                <tbody>{view.items?.map((it,i)=>(<tr key={i} className="border-b border-forest-100"><td className="py-2">{it.name}</td><td className="text-right num">{it.qty}</td><td className="text-right num">{formatIDR(it.unit_price)}</td><td className="text-right num">{formatIDR(it.line_total)}</td></tr>))}</tbody>
              </table>
              <div className="flex justify-between pt-2"><span className="text-forest-400">Subtotal</span><span className="num">{formatIDR(view.subtotal)}</span></div>
              <div className="flex justify-between"><span className="text-forest-400">PPN</span><span className="num">{formatIDR(view.tax)}</span></div>
              <div className="flex justify-between font-display text-lg border-t border-forest-100 pt-2"><span>Total</span><span className="num">{formatIDR(view.total)}</span></div>
              {(view.paid_amount||0) > 0 && <>
                <div className="flex justify-between text-[#4A6B53]"><span>Terbayar</span><span className="num">{formatIDR(view.paid_amount)}</span></div>
                <div className="flex justify-between font-medium text-[#C45A3D]"><span>Sisa Tagihan</span><span className="num" data-testid="outstanding">{formatIDR(view.outstanding)}</span></div>
              </>}
              {view.notes && <div className="text-forest-400 pt-2 border-t border-forest-100">Catatan: {view.notes}</div>}

              {payments.length > 0 && (
                <div className="pt-4">
                  <div className="label-tag mb-2">Riwayat Pembayaran</div>
                  <table className="w-full text-xs">
                    <thead><tr className="border-b border-forest-100"><th className="text-left py-1 label-tag">Tanggal</th><th className="text-left label-tag">Metode</th><th className="text-left label-tag">Ref</th><th className="text-left label-tag">Sumber</th><th className="text-right label-tag">Jumlah</th><th></th></tr></thead>
                    <tbody>{payments.map(p => (
                      <tr key={p.id} className="border-b border-forest-100">
                        <td className="py-1">{formatDate(p.date)}</td>
                        <td>{p.method}</td>
                        <td>{p.reference||"-"}</td>
                        <td>{p.source==="customer"?<Badge className="bg-[#D99C3B] text-white rounded-sm text-[9px]">Klien</Badge>:<Badge variant="outline" className="rounded-sm text-[9px]">Admin</Badge>}</td>
                        <td className="text-right num">{formatIDR(p.amount)}</td>
                        <td><button onClick={()=>delPayment(p.id)} className="text-[#C45A3D] p-1"><Trash2 size={12}/></button></td>
                      </tr>
                    ))}</tbody>
                  </table>
                </div>
              )}
            </div>
          </>}
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default function SalesInvoices() { return <InvoicePage kind="sales" title="Invoice Penjualan" />; }
